import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaExclamationTriangle } from 'react-icons/fa';
import { API_BASE, capitalizeFirstLetter } from '../utils/helpers';

function payTone(s) {
  const v = (s || '').toLowerCase();
  if (v === 'paid') return 'bg-emerald-50 text-emerald-700';
  if (v === 'pending') return 'bg-amber-50 text-amber-700';
  if (v === 'refunded') return 'bg-rose-50 text-rose-700';
  return 'bg-slate-100 text-slate-600';
}

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [intake, setIntake] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const [oRes, iRes] = await Promise.all([
        axios.get(`${API_BASE}/api/shopify/orders/${orderId}`),
        axios.post(`${API_BASE}/api/orders/intake/lookup`, { orderIds: [orderId] }).catch(() => ({ data: {} })),
      ]);
      setOrder(oRes.data.order || null);
      setIntake((iRes.data?.map || {})[String(orderId)] || {});
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to load order.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const money = (v, c) => `${parseFloat(v || 0).toFixed(2)} ${c || order?.currency || 'QAR'}`;
  const items = order?.line_items || [];
  const custName =
    intake.customer_name ||
    [order?.customer?.first_name, order?.customer?.last_name].filter(Boolean).join(' ') ||
    '—';
  const email = intake.email || order?.email || '—';
  const a = intake.address || {};
  const addr = [a.address1, a.address2, a.city, a.province, a.zip, a.country].filter(Boolean).join(', ');

  return (
    <div>
      <button className="ui-btn-ghost mb-4 px-3 py-1.5 text-sm" onClick={() => navigate('/orders')}>
        <FaArrowLeft className="text-xs" /> Back to Orders
      </button>

      {loading ? (
        <div className="ui-card h-48 animate-pulse" />
      ) : error ? (
        <div className="ui-card p-6 text-sm text-rose-600">{error}</div>
      ) : !order ? (
        <div className="ui-card p-6 text-sm text-amber-600">Order not found.</div>
      ) : (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold text-slate-900">{order.name || order.id}</h2>
            <span className={`ui-badge ${payTone(order.financial_status)}`}>
              {capitalizeFirstLetter(order.financial_status || 'unknown')}
            </span>
            {order.fulfillment_status && (
              <span className="ui-badge bg-slate-100 text-slate-600">
                {capitalizeFirstLetter(order.fulfillment_status)}
              </span>
            )}
            {intake.placeholder && (
              <span className="ui-badge bg-amber-50 text-amber-700">⚠ Items to confirm</span>
            )}
            <span className="ml-auto text-sm text-slate-500">
              {order.created_at ? new Date(order.created_at).toLocaleString() : ''}
            </span>
          </div>

          {intake.placeholder && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <FaExclamationTriangle className="mt-0.5 shrink-0" />
              <span>
                This order was placed on an older app version that didn't send the item list, so the
                items below are a placeholder. Please confirm the actual items with the customer.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Items */}
            <div className="lg:col-span-2">
              <div className="ui-card overflow-hidden">
                <div className="border-b border-slate-200 px-5 py-3">
                  <h3 className="text-sm font-semibold text-slate-900">Items ({items.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="ui-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr><td colSpan={4} className="py-8 text-center text-slate-400">No items.</td></tr>
                      ) : items.map((li) => (
                        <tr key={li.id || li.variant_id || li.title}>
                          <td className="font-medium text-slate-900">{li.title}{li.variant_title ? ` — ${li.variant_title}` : ''}</td>
                          <td>{li.quantity}</td>
                          <td className="whitespace-nowrap">{money(li.price)}</td>
                          <td className="whitespace-nowrap text-right font-medium text-slate-900">
                            {money((parseFloat(li.price || 0) * (li.quantity || 1)))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
                  <span className="text-sm text-slate-500">Total</span>
                  <span className="text-base font-semibold text-slate-900">{money(order.total_price)}</span>
                </div>
              </div>
            </div>

            {/* Customer + delivery */}
            <div className="space-y-5">
              <div className="ui-card p-5">
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Customer</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4"><dt className="text-slate-500">Name</dt><dd className="text-slate-800">{custName}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-slate-500">Email</dt><dd className="truncate text-slate-800">{email}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-slate-500">Phone</dt><dd className="text-slate-800">{intake.phone || '—'}</dd></div>
                </dl>
              </div>
              <div className="ui-card p-5">
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Delivery address</h3>
                <p className="text-sm text-slate-700">{addr || '—'}</p>
                {order.note && (
                  <>
                    <h3 className="mb-1 mt-4 text-sm font-semibold text-slate-900">Note</h3>
                    <p className="text-sm text-slate-600">{order.note}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
